import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import api, { API_BASE } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { PencilSimple, Link as LinkIcon, Plus, Trash, Check, X, ChartLine, ChatCircle, SignOut, Sliders, Golf } from '@phosphor-icons/react';
import { toast } from 'sonner';
import VenmoDonate from '@/components/VenmoDonate';

function PostsGrid({ posts, isOwn, isAdmin, onDelete, onEdit }) {
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState(null);
  const [editCaption, setEditCaption] = useState('');

  const startEdit = (post) => {
    setEditingId(post.post_id);
    setEditCaption(post.caption || '');
  };

  const saveEdit = async (postId) => {
    try {
      await onEdit(postId, { caption: editCaption });
      setEditingId(null);
      toast.success('Post updated');
    } catch { toast.error('Update failed'); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try { await onDelete(postId); toast.success('Post deleted'); }
    catch { toast.error('Delete failed'); }
  };

  if (!posts.length) {
    return <p className="text-center text-muted-foreground text-sm py-12 font-sans">No posts yet</p>;
  }
  return (
    <div className="grid grid-cols-3 gap-px bg-border" data-testid="profile-posts-grid">
      {posts.map(post => (
        <div key={post.post_id} className="aspect-square bg-muted relative overflow-hidden group" data-testid={`profile-post-${post.post_id}`}>
          {post.media_type === 'video' ? (
            <video src={`${API_BASE}/api/media/${post.media_path}`} className="w-full h-full object-cover" muted preload="metadata" />
          ) : (
            <img src={`${API_BASE}/api/media/${post.media_path}`} alt="" className="w-full h-full object-cover" loading="lazy" />
          )}
          {/* Hover overlay with edit/delete */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-200 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100">
            <span className="text-white text-xs font-sans font-semibold">{post.likes_count || 0} likes</span>
            {(isOwn || isAdmin) && (
              <div className="flex gap-2 mt-2">
                <button onClick={() => startEdit(post)} className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30" data-testid={`grid-edit-${post.post_id}`}>
                  <PencilSimple size={14} weight="bold" />
                </button>
                <button onClick={() => handleDelete(post.post_id)} className="w-8 h-8 rounded-full bg-red-500/30 flex items-center justify-center text-red-300 hover:bg-red-500/50" data-testid={`grid-delete-${post.post_id}`}>
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            )}
          </div>
          {/* Inline edit modal */}
          {editingId === post.post_id && (
            <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center p-3 z-10">
              <textarea
                value={editCaption}
                onChange={(e) => setEditCaption(e.target.value)}
                className="w-full bg-black/50 text-white text-xs font-sans rounded p-2 resize-none focus:outline-none"
                rows={2}
                data-testid={`grid-edit-input-${post.post_id}`}
              />
              <div className="flex gap-2 mt-2">
                <button onClick={() => saveEdit(post.post_id)} className="w-7 h-7 rounded-full bg-green-500/30 flex items-center justify-center text-green-300" data-testid={`grid-edit-save-${post.post_id}`}>
                  <Check size={12} weight="bold" />
                </button>
                <button onClick={() => setEditingId(null)} className="w-7 h-7 rounded-full bg-red-500/30 flex items-center justify-center text-red-300" data-testid={`grid-edit-cancel-${post.post_id}`}>
                  <X size={12} weight="bold" />
                </button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AffiliateLinks({ links, isOwn, onAdd, onDelete }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', url: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleAdd = async () => {
    if (!form.title.trim() || !form.url.trim()) { toast.error('Title and URL are required'); return; }
    setSubmitting(true);
    try {
      await onAdd(form);
      setForm({ title: '', url: '', description: '' });
      setShowForm(false);
    } catch {} finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3 py-4" data-testid="affiliate-links-section">
      {isOwn && (
        <div className="flex justify-end">
          <button
            onClick={() => setShowForm(!showForm)}
            className="text-xs text-primary font-semibold font-sans tracking-wide uppercase flex items-center gap-1 hover:text-[#083313] transition-colors"
            data-testid="add-affiliate-link-btn"
          >
            <Plus size={14} weight="bold" /> Add Link
          </button>
        </div>
      )}

      {showForm && (
        <div className="border border-border rounded-sm p-4 space-y-3 bg-muted/30" data-testid="affiliate-link-form">
          <input
            value={form.title}
            onChange={(e) => setForm(p => ({ ...p, title: e.target.value }))}
            placeholder="Link title"
            className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-2 text-sm font-sans"
            data-testid="affiliate-link-title-input"
          />
          <input
            value={form.url}
            onChange={(e) => setForm(p => ({ ...p, url: e.target.value }))}
            placeholder="https://..."
            className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-2 text-sm font-sans"
            data-testid="affiliate-link-url-input"
          />
          <input
            value={form.description}
            onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="Description (optional)"
            className="w-full bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 px-0 py-2 text-sm font-sans"
            data-testid="affiliate-link-desc-input"
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowForm(false)} className="p-2 text-muted-foreground hover:text-foreground">
              <X size={18} />
            </button>
            <button
              onClick={handleAdd}
              disabled={submitting}
              className="p-2 text-primary hover:text-[#083313]"
              data-testid="affiliate-link-save-btn"
            >
              <Check size={18} weight="bold" />
            </button>
          </div>
        </div>
      )}

      {links.length === 0 && !showForm && (
        <p className="text-center text-muted-foreground text-sm py-12 font-sans">No links shared yet</p>
      )}

      {links.map(link => (
        <a
          key={link.link_id}
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full border border-border rounded-sm p-4 hover:border-primary/30 hover:-translate-y-[1px] transition-all duration-300 group"
          data-testid={`affiliate-link-${link.link_id}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center flex-shrink-0">
                <LinkIcon size={16} weight="regular" className="text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold font-sans text-foreground truncate group-hover:text-primary transition-colors">
                  {link.title}
                </p>
                {link.description && <p className="text-xs text-muted-foreground truncate">{link.description}</p>}
              </div>
            </div>
            {isOwn && (
              <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(link.link_id); }}
                className="p-1 text-muted-foreground hover:text-destructive transition-colors ml-2 flex-shrink-0"
                data-testid={`delete-affiliate-link-${link.link_id}`}
              >
                <Trash size={16} />
              </button>
            )}
          </div>
        </a>
      ))}
    </div>
  );
}

function FeedPreferences() {
  const [prefs, setPrefs] = useState({ show_videos: true, show_youtube: true, show_rss: true, rss_sources: {} });
  const [rssSources, setRssSources] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/users/me/feed-preferences').then(({ data }) => setPrefs(data)).catch(() => {});
    api.get('/rss/sources').then(({ data }) => setRssSources(data.sources || [])).catch(() => {});
  }, []);

  const toggle = async (key) => {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    setSaving(true);
    try {
      await api.put('/users/me/feed-preferences', {
        show_videos: updated.show_videos,
        show_youtube: updated.show_youtube,
        show_rss: updated.show_rss,
        rss_sources: updated.rss_sources,
      });
    } catch { toast.error('Could not save preference'); }
    finally { setSaving(false); }
  };

  const toggleRssSource = async (sourceKey) => {
    const currentSources = prefs.rss_sources || {};
    const updated = {
      ...prefs,
      rss_sources: { ...currentSources, [sourceKey]: !currentSources[sourceKey] },
    };
    setPrefs(updated);
    setSaving(true);
    try {
      await api.put('/users/me/feed-preferences', {
        show_videos: updated.show_videos,
        show_youtube: updated.show_youtube,
        show_rss: updated.show_rss,
        rss_sources: updated.rss_sources,
      });
    } catch { toast.error('Could not save preference'); }
    finally { setSaving(false); }
  };

  const mainOptions = [
    { key: 'show_videos', label: 'Community Videos', desc: 'Posts from other golfers' },
    { key: 'show_youtube', label: 'YouTube Golf Shorts', desc: 'Trending golf content from YouTube' },
    { key: 'show_rss', label: 'Golf News (Master)', desc: 'Enable/disable all news sources' },
  ];

  return (
    <div className="space-y-1" data-testid="feed-preferences-panel">
      <p className="text-muted-foreground text-xs font-sans px-1 mb-3">Choose what appears in your feed</p>

      {mainOptions.map(opt => (
        <button
          key={opt.key}
          onClick={() => toggle(opt.key)}
          className="w-full flex items-center justify-between px-4 py-3 rounded-sm hover:bg-muted/30 transition-colors"
          data-testid={`pref-toggle-${opt.key}`}
        >
          <div className="text-left">
            <p className="text-sm font-sans font-semibold text-foreground">{opt.label}</p>
            <p className="text-xs font-sans text-muted-foreground mt-0.5">{opt.desc}</p>
          </div>
          <div className={`w-10 h-6 rounded-full flex items-center px-0.5 transition-colors duration-200 ${prefs[opt.key] ? 'bg-[#1B4332]' : 'bg-muted'}`}>
            <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${prefs[opt.key] ? 'translate-x-4' : 'translate-x-0'}`} />
          </div>
        </button>
      ))}

      {/* Granular RSS source toggles */}
      {prefs.show_rss && rssSources.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border" data-testid="rss-source-toggles">
          <p className="text-muted-foreground text-[10px] font-sans uppercase tracking-wider px-4 mb-2 font-semibold">News Sources</p>
          {rssSources.map(src => {
            const isOn = prefs.rss_sources?.[src.key] !== false;
            return (
              <button
                key={src.key}
                onClick={() => toggleRssSource(src.key)}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-sm hover:bg-muted/30 transition-colors"
                data-testid={`rss-toggle-${src.key}`}
              >
                <span className="text-sm font-sans text-foreground">{src.name}</span>
                <div className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors duration-200 ${isOn ? 'bg-[#D4AF37]' : 'bg-muted'}`}>
                  <div className={`w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${isOn ? 'translate-x-[14px]' : 'translate-x-0'}`} />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {saving && <p className="text-xs text-muted-foreground font-sans text-center mt-2">Saving...</p>}
    </div>
  );
}

function GolfBag() {
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editDist, setEditDist] = useState('');

  useEffect(() => {
    api.get('/golf/bag')
      .then(({ data }) => setClubs(data.clubs || []))
      .catch(() => toast.error('Could not load golf bag'))
      .finally(() => setLoading(false));
  }, []);

  const saveClubs = async (updated) => {
    setClubs(updated);
    try {
      await api.put('/golf/bag', { clubs: updated });
    } catch { toast.error('Could not save'); }
  };

  const startEdit = (idx) => { setEditing(idx); setEditDist(String(clubs[idx].avg_distance)); };
  const commitEdit = () => {
    if (editing === null) return;
    const updated = [...clubs];
    updated[editing] = { ...updated[editing], avg_distance: parseInt(editDist) || 0 };
    saveClubs(updated);
    setEditing(null);
  };
  const removeClub = (idx) => saveClubs(clubs.filter((_, i) => i !== idx));
  const addClub = () => saveClubs([...clubs, { name: 'New Club', abbr: 'NC', avg_distance: 150 }]);

  if (loading) return <div className="py-8 text-center text-muted-foreground text-sm">Loading bag...</div>;

  return (
    <div className="space-y-1" data-testid="golf-bag-panel">
      <p className="text-muted-foreground text-xs font-sans px-1 mb-3">Your clubs and average distances</p>
      {clubs.map((club, i) => (
        <div key={i} className="flex items-center justify-between px-3 py-2.5 rounded-sm hover:bg-muted/30" data-testid={`bag-club-${i}`}>
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-[#1B4332] text-white flex items-center justify-center text-[10px] font-bold font-sans">{club.abbr}</span>
            <span className="text-sm font-sans font-medium text-foreground">{club.name}</span>
          </div>
          <div className="flex items-center gap-2">
            {editing === i ? (
              <>
                <input type="number" value={editDist} onChange={e => setEditDist(e.target.value)} className="w-16 px-2 py-1 border border-border rounded-sm text-sm text-center" autoFocus />
                <button onClick={commitEdit} className="text-green-600"><Check size={14} /></button>
              </>
            ) : (
              <>
                <span className="text-sm font-sans font-bold text-foreground min-w-[40px] text-right">{club.avg_distance > 0 ? `${club.avg_distance}y` : '—'}</span>
                <button onClick={() => startEdit(i)} className="text-muted-foreground hover:text-foreground"><PencilSimple size={14} /></button>
                <button onClick={() => removeClub(i)} className="text-muted-foreground hover:text-destructive"><Trash size={12} /></button>
              </>
            )}
          </div>
        </div>
      ))}
      <button onClick={addClub} className="w-full mt-3 py-2 text-xs font-sans font-semibold text-[#1B4332] hover:bg-muted/30 rounded-sm" data-testid="add-club-btn">
        + Add Club
      </button>
    </div>
  );
}


export default function ProfilePage() {
  const { userId } = useParams();
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [links, setLinks] = useState([]);
  const [tab, setTab] = useState('posts');
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', bio: '' });

  const targetId = userId || currentUser?.user_id;
  const isOwn = currentUser && targetId === currentUser.user_id;

  const fetchProfile = useCallback(async () => {
    if (!targetId) return;
    try {
      const [profileRes, postsRes, linksRes] = await Promise.all([
        api.get(`/users/${targetId}`),
        api.get(`/posts?user_id=${targetId}&limit=50`),
        api.get(`/affiliate-links/${targetId}`),
      ]);
      setProfile(profileRes.data);
      setPosts(postsRes.data.posts);
      setLinks(linksRes.data);
      setEditForm({ name: profileRes.data.name || '', bio: profileRes.data.bio || '' });
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [targetId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  if (!targetId && !currentUser) return <Navigate to="/auth" replace />;

  const handleFollow = async () => {
    if (!currentUser) { navigate('/auth'); return; }
    try {
      if (profile.is_following) {
        await api.delete(`/users/${targetId}/follow`);
        setProfile(p => ({ ...p, is_following: false, followers_count: p.followers_count - 1 }));
      } else {
        await api.post(`/users/${targetId}/follow`);
        setProfile(p => ({ ...p, is_following: true, followers_count: p.followers_count + 1 }));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  const handleEditSave = async () => {
    try {
      const { data } = await api.put('/users/me', editForm);
      setProfile(p => ({ ...p, ...data }));
      setEditMode(false);
      toast.success('Profile updated');
    } catch {
      toast.error('Update failed');
    }
  };

  const handleAddLink = async (form) => {
    const { data } = await api.post('/affiliate-links', form);
    setLinks(prev => [data, ...prev]);
    toast.success('Link added');
  };

  const handleDeleteLink = async (linkId) => {
    await api.delete(`/affiliate-links/${linkId}`);
    setLinks(prev => prev.filter(l => l.link_id !== linkId));
    toast.success('Link removed');
  };

  const handleDeletePost = async (postId) => {
    await api.delete(`/posts/${postId}`);
    setPosts(prev => prev.filter(p => p.post_id !== postId));
  };

  const handleEditPost = async (postId, updates) => {
    const { data } = await api.put(`/posts/${postId}`, updates);
    setPosts(prev => prev.map(p => p.post_id === postId ? { ...p, ...data } : p));
  };

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-background">
        <div className="animate-pulse space-y-3 text-center">
          <div className="w-20 h-20 rounded-full bg-muted mx-auto" />
          <div className="w-32 h-4 bg-muted rounded mx-auto" />
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-background">
        <p className="text-muted-foreground font-sans">User not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background" data-testid="profile-page">
      <div className="max-w-md mx-auto px-6 py-10">
        {/* Header */}
        <div className="text-center space-y-4" data-testid="profile-header">
          <Avatar className="w-24 h-24 mx-auto border-2 border-border">
            <AvatarImage src={profile.picture} alt={profile.name} />
            <AvatarFallback className="bg-primary/10 text-primary font-serif text-2xl">
              {(profile.name || 'U').charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>

          {editMode ? (
            <div className="space-y-3">
              <input
                value={editForm.name}
                onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))}
                className="w-full text-center bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 font-serif text-xl py-1"
                data-testid="profile-edit-name"
              />
              <textarea
                value={editForm.bio}
                onChange={(e) => setEditForm(p => ({ ...p, bio: e.target.value }))}
                placeholder="Write a bio..."
                rows={2}
                className="w-full text-center bg-transparent border-0 border-b border-border focus:border-primary focus:outline-none focus:ring-0 text-sm font-sans py-1 resize-none"
                data-testid="profile-edit-bio"
              />
              <div className="flex justify-center gap-3">
                <Button variant="outline" size="sm" onClick={() => setEditMode(false)} className="rounded-sm" data-testid="profile-edit-cancel">Cancel</Button>
                <Button size="sm" onClick={handleEditSave} className="rounded-sm bg-primary" data-testid="profile-edit-save">Save</Button>
              </div>
            </div>
          ) : (
            <>
              <h1 className="font-serif text-2xl text-foreground font-medium">{profile.name}</h1>
              {profile.bio && <p className="text-sm text-muted-foreground font-sans max-w-xs mx-auto">{profile.bio}</p>}
            </>
          )}

          {/* Stats */}
          <div className="flex justify-center gap-8 py-4">
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground font-sans">{posts.length}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-sans">Posts</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground font-sans">{profile.followers_count || 0}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-sans">Followers</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-semibold text-foreground font-sans">{profile.following_count || 0}</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wide font-sans">Following</p>
            </div>
          </div>

          {/* Actions */}
          {isOwn ? (
            !editMode && (
              <div className="grid grid-cols-2 gap-2 px-2">
                <Button
                  variant="outline"
                  onClick={() => setEditMode(true)}
                  className="rounded-sm border-border gap-1.5 text-xs font-sans w-full"
                  data-testid="profile-edit-btn"
                >
                  <PencilSimple size={14} /> Edit Profile
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/scorecard')}
                  className="rounded-sm border-border gap-1.5 text-xs font-sans w-full"
                  data-testid="profile-scorecard-btn"
                >
                  <ChartLine size={14} /> Scorecard
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/messages')}
                  className="rounded-sm border-border gap-1.5 text-xs font-sans w-full"
                  data-testid="profile-messages-btn"
                >
                  <ChatCircle size={14} /> Messages
                </Button>
                {currentUser?.role === 'admin' && (
                  <Button
                    variant="outline"
                    onClick={() => navigate('/admin')}
                    className="rounded-sm border-border gap-1.5 text-xs font-sans w-full"
                    data-testid="profile-admin-btn"
                  >
                    Admin
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={async () => { await logout(); navigate('/auth'); }}
                  className="rounded-sm border-destructive/50 text-destructive hover:bg-destructive/5 gap-1.5 text-xs font-sans w-full col-span-2"
                  data-testid="profile-signout-btn"
                >
                  <SignOut size={14} /> Sign Out
                </Button>
              </div>
            )
          ) : (
            <Button
              onClick={handleFollow}
              className={`rounded-sm text-sm font-sans ${profile.is_following ? 'bg-muted text-foreground hover:bg-muted/80' : 'bg-primary hover:bg-[#083313]'}`}
              data-testid="profile-follow-btn"
            >
              {profile.is_following ? 'Following' : 'Follow'}
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border mt-8" data-testid="profile-tabs">
          <button
            onClick={() => setTab('posts')}
            className={`flex-1 pb-3 text-xs font-semibold tracking-wide uppercase font-sans transition-colors ${tab === 'posts' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
            data-testid="profile-tab-posts"
          >
            Posts
          </button>
          <button
            onClick={() => setTab('links')}
            className={`flex-1 pb-3 text-xs font-semibold tracking-wide uppercase font-sans transition-colors ${tab === 'links' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
            data-testid="profile-tab-links"
          >
            Links
          </button>
          {isOwn && (
            <button
              onClick={() => setTab('feed')}
              className={`flex-1 pb-3 text-xs font-semibold tracking-wide uppercase font-sans transition-colors flex items-center justify-center gap-1 ${tab === 'feed' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
              data-testid="profile-tab-feed"
            >
              <Sliders size={12} weight="bold" /> Feed
            </button>
          )}
          {isOwn && (
            <button
              onClick={() => setTab('bag')}
              className={`flex-1 pb-3 text-xs font-semibold tracking-wide uppercase font-sans transition-colors flex items-center justify-center gap-1 ${tab === 'bag' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}
              data-testid="profile-tab-bag"
            >
              <Golf size={12} weight="bold" /> Bag
            </button>
          )}
        </div>

        {/* Venmo donate on own profile */}
        {isOwn && (
          <div className="mt-4">
            <VenmoDonate />
          </div>
        )}

        {/* Content */}
        <div className="mt-4">
          {tab === 'posts' ? (
            <PostsGrid posts={posts} isOwn={isOwn} isAdmin={currentUser?.role === 'admin'} onDelete={handleDeletePost} onEdit={handleEditPost} />
          ) : tab === 'links' ? (
            <AffiliateLinks links={links} isOwn={isOwn} onAdd={handleAddLink} onDelete={handleDeleteLink} />
          ) : tab === 'feed' ? (
            <FeedPreferences />
          ) : tab === 'bag' ? (
            <GolfBag />
          ) : null}
        </div>
      </div>
    </div>
  );
}
